type MatchRoutePatternParams = {
    pathname: string;
    pattern: string;
};

type MatchResult = {
    matched: boolean;
    params: Record<string, string>;
};

/**
 * Matches a pathname against a route pattern and extracts parameters.
 *
 * Examples:
 * - pathname: '/agents/agent_123/workflows'
 *   pattern: '/agents/:agentId/workflows'
 *   result: { matched: true, params: { agentId: 'agent_123' } }
 *
 * - pathname: '/agents/agent_123/workflows/wf_1/versions/v_1/builder'
 *   pattern: '/agents/:agentId/workflows/:workflowId/versions/:versionId/builder'
 *   result: { matched: true, params: { agentId: 'agent_123', workflowId: 'wf_1', versionId: 'v_1' } }
 *
 * - pathname: '/agents/agent_123/apps/invoice-builder/templates/t1'
 *   pattern: '/agents/:agentId/apps/:appSlug/*'
 *   result: { matched: true, params: { agentId: 'agent_123', appSlug: 'invoice-builder', '*': 'templates/t1' } }
 */
const matchRoutePattern = ({ pathname, pattern }: MatchRoutePatternParams): MatchResult => {
    const pathnameSegments = pathname.split('/').filter(Boolean);
    const patternSegments = pattern.split('/').filter(Boolean);
    const hasSplat = patternSegments[patternSegments.length - 1] === '*';
    const requiredPatternSegments = hasSplat ? patternSegments.slice(0, -1) : patternSegments;

    if (hasSplat && pathnameSegments.length < requiredPatternSegments.length) {
        return { matched: false, params: {} };
    }

    if (!hasSplat && pathnameSegments.length !== patternSegments.length) {
        return { matched: false, params: {} };
    }

    const params: Record<string, string> = {};

    for (let segmentIndex = 0; segmentIndex < requiredPatternSegments.length; segmentIndex++) {
        const patternSegment = requiredPatternSegments[segmentIndex];
        const pathnameSegment = pathnameSegments[segmentIndex];

        if (patternSegment.startsWith(':')) {
            params[patternSegment.slice(1)] = pathnameSegment;
        } else if (patternSegment !== pathnameSegment) {
            return { matched: false, params: {} };
        }
    }

    if (hasSplat) {
        params['*'] = pathnameSegments.slice(requiredPatternSegments.length).join('/');
    }

    return { matched: true, params };
};

export default matchRoutePattern;
